import cx from 'classnames';

type Color = 'default' | 'muted' | 'error' | 'inherit' | 'accent' | 'points' | 'success';

export type TextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type BaseTextProps = {
  /** Element the Text component renders as */
  as?: 'p' | 'span';
  /** Optional classname to pass to the Text */
  className?: string;
  /** Text color */
  color?: Color;
  /** Font size */
  size?: TextSize;
};

type TextElement = HTMLParagraphElement | HTMLSpanElement;
type TextProps = React.HTMLAttributes<TextElement> &
  BaseTextProps & { ref?: React.Ref<TextElement> };

export function Text({
  as: Element = 'p',
  children,
  className,
  color = 'default',
  size = 'md',
  ref,
  ...rest
}: React.PropsWithChildren<TextProps>) {
  const textBase = 'text--base';
  return (
    <Element
      {...rest}
      ref={ref as React.Ref<HTMLParagraphElement & HTMLSpanElement>}
      className={cx(textBase, textSizes[size], className, {
        'text-surface-muted': color === 'muted',
        'text-surface-error': color === 'error',
        'text-surface-success': color === 'success',
        'text-current': color === 'inherit',
        'text-surface-accent': color === 'accent',
        'text-points': color === 'points',
      })}
    >
      {children}
    </Element>
  );
}

const textSizes = {
  xs: 'text--xs',
  sm: 'text--sm',
  md: 'text--md',
  lg: 'text--lg',
  xl: 'text--xl',
} as const satisfies Record<TextSize, string>;
